import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../services/api';
import './index.css';

const MetricCodes = () => {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [form] = Form.useForm();

    const fetchCodes = async () => {
        try {
            setLoading(true);
            const response = await api.get('/metriccodes');
            const data = response.data?.data || [];
            setCodes(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Metrik kodları yükleme hatası:', error);
            message.error('Metrik kodları yüklenirken bir hata oluştu');
            setCodes([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCodes();
    }, []);

    const handleSubmit = async (values) => {
        try {
            const formattedValues = {
                ...values,
                variables: values.variables?.map((v, index) => ({
                    name: v.name,
                    order: index + 1
                })) || []
            };

            if (editingCode) {
                await api.put(`/metriccodes/${editingCode.id}`, formattedValues);
                message.success('Metrik kodu güncellendi');
            } else {
                await api.post('/metriccodes', formattedValues);
                message.success('Metrik kodu eklendi');
            }
            setModalVisible(false);
            form.resetFields();
            setEditingCode(null);
            fetchCodes();
        } catch (error) {
            console.error('Metrik kodu işlem hatası:', error);
            message.error(error.response?.data?.error || 'İşlem sırasında bir hata oluştu');
        }
    };

    const handleDelete = async (record) => {
        try {
            await api.delete(`/metriccodes/${record.id}`);
            message.success('Metrik kodu silindi');
            fetchCodes();
        } catch (error) {
            console.error('Metrik kodu silme hatası:', error);
            message.error(error.response?.data?.error || 'Silme işlemi sırasında bir hata oluştu');
        }
    };

    const columns = [
        {
            title: 'Kod',
            dataIndex: 'code',
            key: 'code',
            width: '20%'
        },
        {
            title: 'Açıklama',
            dataIndex: 'description',
            key: 'description',
            width: '30%'
        },
        {
            title: 'Değişkenler',
            dataIndex: 'variables',
            key: 'variables',
            width: '30%',
            render: (variables) => {
                if (!variables || variables.length === 0) {
                    return <span style={{ color: '#999' }}>Değişken yok</span>;
                }
                return variables.map(v => v.name).join(', ');
            }
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: '20%',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditingCode(record);
                            form.setFieldsValue({
                                ...record,
                                variables: record.variables?.map(v => ({ name: v.name })) || []
                            });
                            setModalVisible(true);
                        }}
                    >
                        Düzenle
                    </Button>
                    <Popconfirm
                        title="Bu metrik kodunu silmek istediğinizden emin misiniz?"
                        onConfirm={() => handleDelete(record)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button type="primary" danger icon={<DeleteOutlined />}>
                            Sil
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="metric-codes-container">
            <div className="metric-codes-header">
                <h2>Metrik Kodları</h2>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setEditingCode(null);
                        form.resetFields();
                        setModalVisible(true);
                    }}
                >
                    Yeni Metrik Kodu Ekle
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={codes}
                rowKey="id"
                loading={loading}
                pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Toplam ${total} metrik kodu`
                }}
            />

            <Modal
                title={editingCode ? 'Metrik Kodu Düzenle' : 'Yeni Metrik Kodu Ekle'}
                open={modalVisible}
                onOk={() => form.submit()}
                onCancel={() => {
                    setModalVisible(false);
                    setEditingCode(null);
                    form.resetFields();
                }}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="code"
                        label="Kod"
                        rules={[
                            { required: true, message: 'Lütfen bir kod girin' },
                            { max: 50, message: 'Kod en fazla 50 karakter olabilir' },
                            {
                                pattern: /^[A-Z][A-Z0-9_]*$/,
                                message: 'Kod büyük harf ile başlamalı ve sadece büyük harf, rakam ve alt çizgi içerebilir'
                            }
                        ]}
                    >
                        <Input placeholder="Örn: SHOPIFY_SALES" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Açıklama"
                        rules={[
                            { max: 200, message: 'Açıklama en fazla 200 karakter olabilir' }
                        ]}
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="Metrik kodu için açıklama girin"
                        />
                    </Form.Item>

                    <Form.List name="variables">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'name']}
                                            validateTrigger={['onChange', 'onBlur']}
                                            rules={[
                                                { required: true, message: 'Değişken adı gerekli' },
                                                {
                                                    pattern: /^[a-z][a-z0-9_]*$/,
                                                    message: 'Değişken adı küçük harf ile başlamalı ve sadece harf, rakam ve alt çizgi içermeli'
                                                }
                                            ]}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input placeholder="Değişken adı" />
                                        </Form.Item>
                                        <Button
                                            type="text"
                                            icon={<DeleteOutlined />}
                                            onClick={() => remove(field.name)}
                                            style={{ color: '#ff4d4f' }}
                                        />
                                    </Space>
                                ))}
                                <Button
                                    type="dashed"
                                    onClick={() => add()}
                                    block
                                    icon={<PlusOutlined />}
                                >
                                    Değişken Ekle
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Form>
            </Modal>
        </div>
    );
};

export default MetricCodes;